<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import TelegramInternalApi from 'dashboard/api/telegramInternal';

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const selectedInboxId = ref(null);
const queueItems = ref([]);

const inboxes = useMapGetter('inboxes/getInboxes');

const telegramInboxes = computed(() => {
  return (inboxes.value || []).filter(inbox =>
    String(inbox.channel_type || '')
      .toLowerCase()
      .includes('telegram')
  );
});

const openCount = computed(() => queueItems.value.length);

const ensureBaseData = async () => {
  await store.dispatch('inboxes/get');
};

const fetchConversations = async () => {
  isLoading.value = true;
  try {
    const response = await TelegramInternalApi.getQueue({
      inboxId: selectedInboxId.value,
    });
    const payload = response.data || {};
    queueItems.value = (payload.queue_items || []).map(item => ({
      conversationId: Number(item.conversation_id),
      inboxId: Number(item.inbox_id),
      categoryName: item.category_name,
      ownerName: item.owner_name || t('TELEGRAM_INTERNAL.QUEUE.UNASSIGNED'),
      summary: item.summary || '-',
      hasMentionNotification: Boolean(item.has_mention_notification),
      route: accountScopedRoute('inbox_conversation', {
        conversation_id: item.conversation_id,
      }),
      sourceRoute: accountScopedRoute('conversation_through_inbox', {
        inbox_id: item.inbox_id,
        conversation_id: item.conversation_id,
      }),
    }));
  } finally {
    isLoading.value = false;
  }
};

const closeQueueItem = async item => {
  await TelegramInternalApi.closeQueueItem(item.conversationId);
  queueItems.value = queueItems.value.filter(
    queueItem => queueItem.conversationId !== item.conversationId
  );
};

const resetClosedItems = async () => {
  await TelegramInternalApi.resetQueue();
  await fetchConversations();
};

onMounted(async () => {
  await ensureBaseData();
  if (telegramInboxes.value.length) {
    selectedInboxId.value = Number(telegramInboxes.value[0].id);
    await fetchConversations();
  }
});

watch(selectedInboxId, () => {
  fetchConversations();
});
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <div class="rounded-lg border border-n-weak bg-n-solid-2 p-4">
      <h1 class="text-base font-semibold text-n-slate-12">
        {{ t('TELEGRAM_INTERNAL.QUEUE.TITLE') }}
      </h1>
      <p class="text-sm text-n-slate-11">
        {{ t('TELEGRAM_INTERNAL.QUEUE.DESCRIPTION') }}
      </p>
    </div>

    <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <div
        class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between"
      >
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('TELEGRAM_INTERNAL.FILTER.GROUP') }}
          <select
            v-model="selectedInboxId"
            class="rounded-md border border-n-weak bg-n-background px-2 py-2"
          >
            <option :value="null">
              {{ t('TELEGRAM_INTERNAL.FILTER.SELECT_GROUP') }}
            </option>
            <option
              v-for="inbox in telegramInboxes"
              :key="inbox.id"
              :value="Number(inbox.id)"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>
        <div class="flex items-center gap-2">
          <div
            class="rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12"
          >
            {{ t('TELEGRAM_INTERNAL.QUEUE.OPEN_ITEMS') }}: {{ openCount }}
          </div>
          <button
            type="button"
            class="rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12"
            @click="resetClosedItems"
          >
            {{ t('TELEGRAM_INTERNAL.QUEUE.RESET') }}
          </button>
        </div>
      </div>
    </div>

    <div class="overflow-x-auto rounded-lg border border-n-weak bg-n-solid-1">
      <table class="min-w-full text-sm">
        <thead class="bg-n-alpha-2 text-left text-n-slate-11">
          <tr>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.TABLE.CASE') }}</th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.TABLE.CATEGORY') }}
            </th>
            <th class="px-3 py-2">{{ t('TELEGRAM_INTERNAL.TABLE.OWNER') }}</th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.TABLE.SUMMARY') }}
            </th>
            <th class="px-3 py-2">
              {{ t('TELEGRAM_INTERNAL.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in queueItems"
            :key="item.conversationId"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="px-3 py-2">
              <div class="flex flex-col gap-1">
                <RouterLink
                  class="text-n-brand hover:underline"
                  :to="item.route"
                >
                  #{{ item.conversationId }}
                </RouterLink>
                <span
                  v-if="item.hasMentionNotification"
                  class="text-xs text-n-ruby-11"
                >
                  {{ t('TELEGRAM_INTERNAL.QUEUE.MENTIONED_YOU') }}
                </span>
              </div>
            </td>
            <td class="px-3 py-2">{{ item.categoryName }}</td>
            <td class="px-3 py-2">{{ item.ownerName }}</td>
            <td class="px-3 py-2">{{ item.summary }}</td>
            <td class="px-3 py-2">
              <div class="flex gap-2">
                <RouterLink
                  :to="item.sourceRoute"
                  class="rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12"
                >
                  {{ t('TELEGRAM_INTERNAL.TABLE.OPEN_SOURCE') }}
                </RouterLink>
                <button
                  type="button"
                  class="rounded-md bg-n-brand px-2 py-1 text-xs font-medium text-white"
                  @click="closeQueueItem(item)"
                >
                  {{ t('TELEGRAM_INTERNAL.TABLE.CLOSE_CASE') }}
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!queueItems.length && !isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="5">
              {{ t('TELEGRAM_INTERNAL.TABLE.EMPTY') }}
            </td>
          </tr>
          <tr v-if="isLoading">
            <td class="px-3 py-6 text-n-slate-11" colspan="5">
              {{ t('TELEGRAM_INTERNAL.LOADING') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
